import { readingOrder } from './box-geometry'

// A split marks the pages a new document starts after, the pages left out, and the boxed receipts on pages
// that hold several. A boxed page keeps only its boxes, each its own document. Pages are 1-based.
export const MAX_SOURCE_PAGES = 200
export const MAX_SOURCE_BYTES = 50 * 1024 * 1024
export const MAX_SEGMENT_PAGES = 10
export const MAX_SEGMENT_BYTES = 10 * 1024 * 1024

export const oneDocument = (pageCount) => ({ pageCount, breaks: [], skipped: [], boxes: {} })

export const everyPageSeparate = (pageCount) => ({
  pageCount,
  breaks: Array.from({ length: pageCount - 1 }, (_, i) => i + 1),
  skipped: [],
  boxes: {}
})

const toggle = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value].sort((a, b) => a - b)

export const toggleBreak = (split, afterPage) => ({ ...split, breaks: toggle(split.breaks, afterPage) })

export const toggleSkip = (split, page) => ({ ...split, skipped: toggle(split.skipped, page) })

export const boxesOn = (split, page) => split.boxes[page] || []

export const setBoxes = (split, page, boxes) => ({ ...split, boxes: { ...split.boxes, [page]: boxes } })

export const isBoxed = (split, page) => boxesOn(split, page).length > 0

export const toSegments = (split) => {
  const segments = []
  let current = []
  const close = () => {
    if (current.length > 0) segments.push({ pieces: current })
    current = []
  }

  for (let page = 1; page <= split.pageCount; page++) {
    if (split.skipped.includes(page)) {
      if (split.breaks.includes(page)) close()
      continue
    }
    if (isBoxed(split, page)) {
      close()
      readingOrder(boxesOn(split, page)).forEach((box) => segments.push({ pieces: [{ page, box }] }))
      continue
    }
    current.push({ page })
    if (split.breaks.includes(page)) close()
  }
  close()

  return segments
}

// Which document each whole page and each box lands in, for coloring and labels.
export const segmentLabels = (split) => {
  const pages = {}
  const boxes = {}
  toSegments(split).forEach((segment, i) =>
    segment.pieces.forEach(({ page, box }) => {
      if (box) boxes[box.id] = i
      else pages[page] = i
    })
  )
  return { pages, boxes }
}

// "page 4", "pages 1–3", or "pages 1–2, 5" when a skipped page falls inside a document.
export const describePages = (pages) => {
  const sorted = [...new Set(pages)].sort((a, b) => a - b)
  const runs = []
  sorted.forEach((page) => {
    const last = runs[runs.length - 1]
    if (last && page === last[1] + 1) last[1] = page
    else runs.push([page, page])
  })
  const text = runs.map(([first, last]) => (first === last ? `${first}` : `${first}–${last}`)).join(', ')
  return `${sorted.length === 1 ? 'page' : 'pages'} ${text}`
}

export const segmentProblems = (segments, noun) => {
  if (segments.length === 0) return [`Every page is skipped, so there is no ${noun} to upload.`]

  return segments
    .map((segment, i) =>
      segment.pieces.length > MAX_SEGMENT_PAGES
        ? `${capitalize(noun)} ${i + 1} has ${segment.pieces.length} pages; the maximum is ${MAX_SEGMENT_PAGES}.`
        : null
    )
    .filter(Boolean)
}

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1)
